/**
 * mapEventToFormState — reverse map a `getEvent` payload into the local
 * `EventFormBody` form-state shape used by Edit.
 *
 * Mirrors `buildEventPayload` in shape so a round-trip
 * (event → form → payload → server) stays stable.
 *
 * Notes:
 *   - `daysOfWeek` may arrive as the comma-separated string "1,3,5" OR as
 *     an array `["1","3","5"]`. Normalize to string array `["1","3","5"]`.
 *   - Custom field values are reverse-mapped by `typeId` (NOT relation `id`).
 *   - Date custom fields hydrate to `CalendarDate`; numeric/text stay strings.
 *   - `startTime`/`finishTime` arrive as Unix-seconds epoch — convert to
 *     `CalendarDateTime`.
 *
 * @module mapEventToFormState
 */
import { epochToCalendarDateTime, parseYMDToCalendarDate } from './dateTime.js';
import { deriveReserveSeed } from './addToWaiting.js';

const toStringId = v => (v == null ? '' : String(v));

/** Normalize `daysOfWeek` to `string[]` of valid `'0'..'6'` day keys. */
function normalizeDaysOfWeek(raw) {
	let arr = [];
	if (Array.isArray(raw)) {
		arr = raw.map(String);
	} else if (typeof raw === 'string' && raw.trim()) {
		arr = raw
			.split(',')
			.map(s => s.trim())
			.filter(Boolean);
	}
	// Defensive: drop anything that is not a Sun=0..Sat=6 key (e.g. a stray
	// "NaN" from a bad weekday computation must never reach form state).
	return arr.filter(d => /^[0-6]$/.test(d));
}

/**
 * @param {Object} event Normalized detail event (post `normalizeDetailEvent`).
 * @param {Array<{id:number|string,type:string}>} customFields Org's custom field defs.
 * @param {string} [eventLocationTz] IANA tz for the event's location (e.g. 'America/New_York').
 * @returns {Object} Form state matching the `EventFormBody` `useState` shape.
 */
export function mapEventToFormState(event, customFields = [], eventLocationTz) {
	const ev = event || {};

	const startTime = epochToCalendarDateTime(ev.startTime, eventLocationTz);
	const finishTime = epochToCalendarDateTime(ev.finishTime, eventLocationTz);

	const eventType = ev.type === 'maintenance' ? 'maintenance' : 'air';

	const recurring = ev.recurring === true || ev.recurring === '1' || ev.recurring === 1;

	// Reverse-map custom fields by `typeId` — backend builder writes the
	// per-event value list keyed by `typeId`, NOT relation row id. Build a
	// lookup `{ [typeId]: value }` then pull each form field out by its
	// org-custom-field id (which IS the typeId).
	const customValues = {};
	const eventCustomFields = Array.isArray(ev.customFields) ? ev.customFields : [];
	const valueByTypeId = new Map();
	for (const cf of eventCustomFields) {
		const typeId = cf?.typeId ?? cf?.typeID ?? cf?.type?.id ?? null;
		if (typeId == null) continue;
		valueByTypeId.set(String(typeId), cf?.value ?? '');
	}
	for (const def of customFields) {
		const v = valueByTypeId.get(String(def.id));
		if (v == null || v === '') continue;
		if (def.type === 'date') {
			const cd = parseYMDToCalendarDate(String(v));
			if (cd) customValues[String(def.id)] = cd;
		} else {
			customValues[String(def.id)] = String(v);
		}
	}

	return {
		formOrgUnique: ev.organizationUnique || '',
		locationTimezoneId: eventLocationTz || ev.locationTimezoneId || '',
		eventType,
		startTime,
		finishTime,
		locationId: toStringId(ev.locationID),
		// Backend ships lowercase camelCase (`pilotId/coPilotId/instructorId`)
		// from Symfony serialization; legacy V1 PHP responses used uppercase
		// (`pilotID/secondInCommandID/instructorID`). Check uppercase first
		// for back-compat, fall back to lowercase. `normalizeEventDetail`
		// (`EventViewModal.js:84-86`) uses the same fallback chain.
		pilotId: toStringId(ev.pilotID ?? ev.pilotId),
		sicPilotId: toStringId(ev.secondInCommandID ?? ev.coPilotId),
		// Form's Instructor Autocomplete uses RESOURCE id (item.id of
		// `EventParamsResourceDto`) as `selectedKey`, NOT user id. The Symfony
		// detail response ships the user id under `ev.instructor.id` / flat
		// `ev.instructorId`, but the matching resource id lives on the
		// instructor row inside `eventResources`. Reading the user id here
		// breaks two things: the Autocomplete renders empty (no matching
		// candidates[].id), AND backend `EventParamsRequestDto.getResources()`
		// auto-appends `instructorID` to `resources[]` → query for a user id
		// in the resource table fails → 400 "Some resources are unavailable".
		instructorId: (() => {
			if (Array.isArray(ev.eventResources)) {
				const instructorRow = ev.eventResources.find(er => {
					const resType = er.resource?.type;
					const typeName = typeof resType === 'object' ? resType?.name : resType;
					return (
						(typeName && String(typeName).toLowerCase().includes('instructor')) ||
						er.resource?.user != null ||
						er.instructorId != null
					);
				});
				if (instructorRow) {
					return toStringId(
						instructorRow.resource?.id ?? instructorRow.resourceID ?? instructorRow.resourceId
					);
				}
			}
			// Fallback to flat shape (legacy V1 payloads sometimes carry
			// `instructorID` already as a resource id at the root level).
			return toStringId(ev.instructorID ?? ev.instructorId);
		})(),
		// Prefer the nested `eventResources` shape (carries type info) so we
		// can drop instructor rows — the backend lazy-populates `instructor`
		// from an eventResource (`Event.php:1027`) and ALSO includes it in the
		// flat `resourceIDs[]`. Leaving it in the resources list causes the
		// Edit form to render an instructor chip and the Save request to
		// emit it in `resources[]` → backend 400 "Some resources are
		// unavailable". Same predicate as `EventViewModal.js:905`.
		resourceIds: (() => {
			if (Array.isArray(ev.eventResources) && ev.eventResources.length) {
				return ev.eventResources
					.filter(er => {
						// Aviation entries (aircraft / simulator) always kept.
						if (er.aircraft || er.resource?.aircraft || er.simulator || er.resource?.simulator)
							return true;
						if (er.resource && String(er.resource?.type) === 'aircraft') return true;
						if (er.resource && String(er.resource?.type) === 'simulator') return true;
						// Drop instructor entries — they have their own field.
						const resType = er.resource?.type;
						const typeName = typeof resType === 'object' ? resType?.name : resType;
						if (typeName && String(typeName).toLowerCase().includes('instructor')) return false;
						if (er.resource?.user || er.instructorId != null) return false;
						return true;
					})
					.map(er => toStringId(er.resourceID ?? er.resourceId ?? er.resource?.id))
					.filter(Boolean);
			}
			// Fallback to the flat shape when `eventResources` is missing.
			return Array.isArray(ev.resourceIDs) && ev.resourceIDs.length
				? ev.resourceIDs.map(toStringId)
				: ev.resourceID != null
					? [toStringId(ev.resourceID)]
					: [];
		})(),
		// Activity Type — form state holds a bare ID string (day-view parity).
		// Field-name varies by endpoint: the month LIST payload ships
		// `activityType` (bare id or nested `{ id, ... }` object), while the
		// single-event DETAIL payload ships `activityTypeId` (bare int). Accept
		// either key and extract the id. Empty string = "Not set".
		activityType: (() => {
			const raw = ev.activityType ?? ev.activityTypeId;
			return toStringId(raw && typeof raw === 'object' ? raw.id : raw);
		})(),
		// Event Sharing — "invite pilots" flag. Form state holds a boolean.
		// Accept the Symfony `eventSharing` field or the legacy `share` field.
		// Explicit `'1'`/`1`/`true` check — the string `'0'` is truthy, so a
		// bare `!!` would wrongly enable it.
		eventSharing: (() => {
			const v = ev.eventSharing ?? ev.share;
			return v === '1' || v === 1 || v === true;
		})(),
		comments: ev.comments || ev.description || '',
		recurring,
		recurringType: ev.recurringType || 'day',
		daysOfWeek: normalizeDaysOfWeek(ev.daysOfWeek),
		repsType: ev.repsType || 'number',
		repsInterval: ev.repsInterval != null ? String(ev.repsInterval) : '1',
		reps: ev.reps != null ? String(ev.reps) : '1',
		recurringEndDate: ev.recurringEndDate
			? parseYMDToCalendarDate(String(ev.recurringEndDate))
			: null,
		customValues,
		// Waiting-list status at open time — seeds the month-view addToWaiting
		// flip so editing an existing waiting-list event submits addToWaiting=true
		// even before the first precheck returns. Only WAITING_LIST (reserve=1)
		// counts; INSTRUCTOR (2) does not.
		reserve: deriveReserveSeed(ev)
	};
}
